// CaptadorSelector.tsx
import { useEffect, useState } from "react";
import { X } from "lucide-react";

import type { PropertyFormData } from "@/components/form-property/property-types";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/use-toast";
import { useDebounce } from "@/hooks/useDebounce";

interface User {
  id: number;
  username: string;
  email: string;
  collaborator: "BROKER" | "ADMIN";
  blocked: boolean;
}

interface CaptadorSelectorProps {
  setFormData: React.Dispatch<React.SetStateAction<PropertyFormData>>;
  selectedCaptadores?: number[];
}

export function CaptadorSelector({
  setFormData,
  selectedCaptadores = [],
}: CaptadorSelectorProps) {
  const { toast } = useToast();
  const [captadorSearchTerm, setCaptadorSearchTerm] = useState("");
  const [filteredCaptadores, setFilteredCaptadores] = useState<User[]>([]);
  const [selectedCaptadoresList, setSelectedCaptadoresList] = useState<User[]>(
    [],
  );
  const debouncedSearchTerm = useDebounce(captadorSearchTerm, 500);
  const route = import.meta.env.VITE_URL_ENDPOINT;

  // Buscar dados dos captadores selecionados ao inicializar
  useEffect(() => {
    if (selectedCaptadores && selectedCaptadores.length > 0) {
      const fetchSelectedCaptadores = async () => {
        try {
          const promises = selectedCaptadores.map((id) => {
            return fetch(`${route}/api/users/${id}`);
          });
          const responses = await Promise.all(promises);
          const users = await Promise.all(responses.map((res) => res.json()));
          setSelectedCaptadoresList(users);
        } catch (error) {
          console.error("❌ Erro ao buscar captadores selecionados:", error);
        }
      };
      fetchSelectedCaptadores();
    } else {
      setSelectedCaptadoresList([]);
    }
  }, [selectedCaptadores, route]);

  // Buscar captadores baseado no termo de pesquisa
  useEffect(() => {
    if (debouncedSearchTerm && debouncedSearchTerm.length >= 2) {
      const fetchFilteredCaptadores = async () => {
        try {
          const response = await fetch(
            `${route}/api/users?filters[username][$containsi]=${debouncedSearchTerm}&filters[blocked][$eq]=false`,
          );
          const result = await response.json();
          // Filtrar captadores que já estão selecionados
          const filtered = result.filter(
            (user: User) =>
              !selectedCaptadoresList.some(
                (selected) => selected.id === user.id,
              ),
          );
          setFilteredCaptadores(filtered);
        } catch (error) {
          console.error("Erro ao buscar captadores:", error);
          toast({
            title: "Erro",
            description: "Erro ao buscar captadores.",
            variant: "destructive",
          });
        }
      };

      fetchFilteredCaptadores();
    } else {
      setFilteredCaptadores([]);
    }
  }, [debouncedSearchTerm, route, toast, selectedCaptadoresList]);

  const handleCaptadorSelect = (captador: User) => {
    const newSelectedList = [...selectedCaptadoresList, captador];
    setSelectedCaptadoresList(newSelectedList);

    // Atualizar formData com os IDs dos captadores
    setFormData((prevState) => ({
      ...prevState,
      captadores: newSelectedList.map((c) => c.id),
    }));

    setCaptadorSearchTerm("");
    setFilteredCaptadores([]);
  };

  const handleCaptadorRemove = (captadorId: number) => {
    const newSelectedList = selectedCaptadoresList.filter(
      (c) => c.id !== captadorId,
    );
    setSelectedCaptadoresList(newSelectedList);

    // Atualizar formData com os IDs dos captadores
    setFormData((prevState) => ({
      ...prevState,
      captadores: newSelectedList.map((c) => c.id),
    }));
  };

  return (
    <div className="mb-5">
      <Label htmlFor="captadores" className="text-gray-600">
        Captador(es)
      </Label>

      {/* Lista de captadores selecionados */}
      {selectedCaptadoresList.length > 0 && (
        <div className="mb-3 space-y-2">
          {selectedCaptadoresList.map((captador) => (
            <div
              key={captador.id}
              className="flex items-center justify-between rounded-lg border border-blue-200 bg-blue-50 p-3"
            >
              <div className="flex-1">
                <div className="font-medium text-gray-800">
                  {captador.username}
                </div>
                <div className="text-sm text-gray-500">{captador.email}</div>
                <div className="text-xs text-gray-400">
                  {captador.collaborator === "ADMIN"
                    ? "Administrador"
                    : "Corretor"}
                </div>
              </div>

              <button
                type="button"
                onClick={() => handleCaptadorRemove(captador.id)}
                className="ml-2 rounded-full p-1 text-red-600 hover:bg-red-100 hover:text-red-800"
                title="Remover captador"
              >
                <X size={16} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Campo de busca */}
      <Input
        type="text"
        id="captadores"
        name="captadores"
        value={captadorSearchTerm}
        onChange={(e) => setCaptadorSearchTerm(e.target.value)}
        placeholder="Digite o nome do captador para adicionar"
        className="w-full"
      />

      {/* Lista de resultados da busca */}
      {debouncedSearchTerm && debouncedSearchTerm.length >= 2 && (
        <div className="mt-1 max-h-48 overflow-y-auto border border-gray-300 bg-white shadow-md">
          {filteredCaptadores.length > 0 ? (
            filteredCaptadores.map((captador) => (
              <div
                key={captador.id}
                onClick={() => handleCaptadorSelect(captador)}
                className="cursor-pointer p-3 hover:bg-gray-100"
              >
                <div className="font-medium">{captador.username}</div>
                <div className="text-sm text-gray-500">{captador.email}</div>
                <div className="text-xs text-gray-400">
                  {captador.collaborator === "ADMIN"
                    ? "Administrador"
                    : "Corretor"}
                </div>
              </div>
            ))
          ) : (
            <div className="p-3 text-gray-500">Nenhum captador encontrado</div>
          )}
        </div>
      )}
    </div>
  );
}
